import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import {
  Send, Eye, Users, TrendingUp, BarChart3, Clock, Loader2,
  ArrowUpRight, ArrowDownRight, Activity, Target, MousePointerClick,
  ShieldAlert, AlertTriangle, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_COLORS = {
  all: "#10b981",
  role: "#6366f1",
  activity: "#f59e0b",
  subscribers: "#ec4899",
  custom: "#8b5cf6",
};

const AUDIENCE_LABELS = {
  all: "All Users",
  role: "By Role",
  activity: "By Activity",
  subscribers: "Subscribers",
  custom: "Custom",
};

const STATUS_STYLES = {
  scheduled: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  sent: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  paused: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  failed: "bg-red-500/10 text-red-600 border-red-500/20",
  sending: "bg-purple-500/10 text-purple-600 border-purple-500/20",
};

export function EmailAnalyticsDashboard({ darkMode: dm }) {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [audienceData, setAudienceData] = useState({ data: [], audience_types: [] });
  const [perMessage, setPerMessage] = useState([]);
  const [clickStats, setClickStats] = useState([]);
  const [bounceStats, setBounceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [cleaningList, setCleaningList] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, trendRes, audRes, msgRes, clickRes, bounceRes] = await Promise.all([
        axios.get(`${API}/admin/email-analytics/summary`, { headers }),
        axios.get(`${API}/admin/email-analytics/trends?days=${days}`, { headers }),
        axios.get(`${API}/admin/email-analytics/audience-breakdown?days=${days}`, { headers }),
        axios.get(`${API}/admin/email-analytics/per-message`, { headers }),
        axios.get(`${API}/admin/email-analytics/click-stats`, { headers }),
        axios.get(`${API}/admin/email-analytics/bounce-stats`, { headers }).catch(() => ({ data: null })),
      ]);
      setSummary(sumRes.data);
      setTrends(trendRes.data);
      setAudienceData(audRes.data);
      setPerMessage(msgRes.data);
      setClickStats(clickRes.data);
      setBounceStats(bounceRes.data);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const hasData = summary && summary.total_sent > 0;

  return (
    <div className="space-y-6" data-testid="email-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            Email Analytics
          </h3>
          <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Track delivery, open rates, click engagement, and audience trends
          </p>
        </div>
        <div className="flex gap-1.5">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                days === d
                  ? "bg-[#e0482d] text-white"
                  : dm ? "bg-gray-700/50 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`analytics-days-${d}-btn`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard dm={dm} icon={Send} label="Total Sent" value={summary?.total_sent ?? 0} accent="text-blue-500" bg="bg-blue-500/10" testId="stat-total-sent" />
        <SummaryCard dm={dm} icon={Eye} label="Total Opens" value={summary?.total_opened ?? 0} accent="text-emerald-500" bg="bg-emerald-500/10" testId="stat-total-opened" />
        <SummaryCard dm={dm} icon={TrendingUp} label="Open Rate" value={`${summary?.open_rate ?? 0}%`} accent="text-amber-500" bg="bg-amber-500/10" testId="stat-open-rate" />
        <SummaryCard dm={dm} icon={MousePointerClick} label="Link Clicks" value={summary?.total_clicks ?? 0} accent="text-pink-500" bg="bg-pink-500/10" testId="stat-total-clicks" />
        <SummaryCard dm={dm} icon={ShieldAlert} label="Bounce Rate" value={`${summary?.bounce_rate ?? 0}%`} accent="text-red-500" bg="bg-red-500/10" testId="stat-bounce-rate" />
      </div>

      {!hasData ? (
        <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${dm ? "border-gray-700" : "border-gray-200"}`}>
          <Activity className={`w-10 h-10 mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
          <p className={`text-sm font-medium ${dm ? "text-gray-400" : "text-gray-500"}`}>No email data yet</p>
          <p className={`text-xs mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>
            Analytics will appear here after sending scheduled messages
          </p>
        </div>
      ) : (
        <>
          {/* Delivery Trend — Line Chart */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-1">
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                <BarChart3 className="w-4 h-4 inline mr-1.5" />
                Delivery & Open Trends
              </CardTitle>
              <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                Daily email sends vs opens over the last {days} days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]" data-testid="trends-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke={dm ? "#2d3748" : "#e5e7eb"} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }}
                      stroke={dm ? "#6b7280" : "#9ca3af"}
                      fontSize={11}
                      tickLine={false}
                    />
                    <YAxis stroke={dm ? "#6b7280" : "#9ca3af"} fontSize={11} allowDecimals={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: dm ? "#1f2937" : "#fff",
                        border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                        borderRadius: "12px",
                        color: dm ? "#f3f4f6" : "#111827",
                        fontSize: 12,
                      }}
                      labelFormatter={v => new Date(v).toLocaleDateString()}
                    />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      type="linear"
                      dataKey="sent"
                      name="Sent"
                      stroke="#818cf8"
                      strokeWidth={2.5}
                      dot={{ r: 2.5, fill: "#818cf8", strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: "#818cf8", stroke: dm ? "#1f2937" : "#fff", strokeWidth: 2 }}
                    />
                    <Line
                      type="linear"
                      dataKey="opened"
                      name="Opened"
                      stroke="#34d399"
                      strokeWidth={2.5}
                      dot={{ r: 2.5, fill: "#34d399", strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: "#34d399", stroke: dm ? "#1f2937" : "#fff", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Two-column: Audience Frequency + Click Radar */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Audience Frequency — Area Chart */}
            {audienceData.data.length > 0 && (
              <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
                <CardHeader className="pb-1">
                  <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                    <Users className="w-4 h-4 inline mr-1.5" />
                    Open Frequency by Audience
                  </CardTitle>
                  <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                    Email opens segmented by audience type
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]" data-testid="audience-frequency-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={audienceData.data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          {audienceData.audience_types.map(aud => (
                            <linearGradient key={aud} id={`grad_${aud}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={AUDIENCE_COLORS[aud] || "#10b981"} stopOpacity={0.4} />
                              <stop offset="95%" stopColor={AUDIENCE_COLORS[aud] || "#10b981"} stopOpacity={0.05} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={dm ? "#374151" : "#e5e7eb"} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={v => { const d = new Date(v); return `${d.getMonth()+1}/${d.getDate()}`; }}
                          stroke={dm ? "#6b7280" : "#9ca3af"}
                          fontSize={10}
                        />
                        <YAxis stroke={dm ? "#6b7280" : "#9ca3af"} fontSize={10} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: dm ? "#1f2937" : "#fff",
                            border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                            borderRadius: "12px",
                            color: dm ? "#f3f4f6" : "#111827",
                            fontSize: 11,
                          }}
                          labelFormatter={v => new Date(v).toLocaleDateString()}
                        />
                        <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
                        {audienceData.audience_types.map(aud => (
                          <Area
                            key={aud}
                            type="linear"
                            dataKey={`${aud}_opened`}
                            name={AUDIENCE_LABELS[aud] || aud}
                            stroke={AUDIENCE_COLORS[aud] || "#10b981"}
                            fill={`url(#grad_${aud})`}
                            strokeWidth={2}
                            stackId="1"
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Click Engagement — Radar Chart */}
            <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
              <CardHeader className="pb-1">
                <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                  <MousePointerClick className="w-4 h-4 inline mr-1.5" />
                  Link Click Engagement
                </CardTitle>
                <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                  Click distribution across email links (total vs unique)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]" data-testid="click-radar-chart">
                  {clickStats.length === 0 ? (
                    <div className={`flex flex-col items-center justify-center h-full ${dm ? "text-gray-500" : "text-gray-400"}`}>
                      <MousePointerClick className="w-8 h-8 mb-2 opacity-30" />
                      <p className="text-xs font-medium">No click data yet</p>
                      <p className="text-[10px] mt-0.5">Clicks will appear after sending emails with links</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={clickStats}>
                        <PolarGrid
                          stroke={dm ? "#374151" : "#d1d5db"}
                          strokeDasharray="3 3"
                        />
                        <PolarAngleAxis
                          dataKey="label"
                          tick={{ fill: dm ? "#9ca3af" : "#6b7280", fontSize: 11 }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, "auto"]}
                          tick={{ fill: dm ? "#6b7280" : "#9ca3af", fontSize: 9 }}
                        />
                        <Radar
                          name="Total Clicks"
                          dataKey="total_clicks"
                          stroke="#6366f1"
                          fill="#6366f1"
                          fillOpacity={0.35}
                          strokeWidth={2}
                        />
                        <Radar
                          name="Unique Clicks"
                          dataKey="unique_clicks"
                          stroke="#ec4899"
                          fill="#ec4899"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                        <Legend
                          iconType="square"
                          wrapperStyle={{ fontSize: 11 }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: dm ? "#1f2937" : "#fff",
                            border: `1px solid ${dm ? "#374151" : "#e5e7eb"}`,
                            borderRadius: "12px",
                            color: dm ? "#f3f4f6" : "#111827",
                            fontSize: 12,
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Per-Message Performance Table */}
          <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                <Activity className="w-4 h-4 inline mr-1.5" />
                Message Performance
              </CardTitle>
              <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
                Individual campaign delivery, engagement, and click stats
              </CardDescription>
            </CardHeader>
            <CardContent>
              {perMessage.length === 0 ? (
                <p className={`text-sm text-center py-8 ${dm ? "text-gray-500" : "text-gray-400"}`}>
                  No messages sent yet
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="per-message-table">
                    <thead>
                      <tr className={`border-b ${dm ? "border-gray-700" : "border-gray-100"}`}>
                        <th className={`text-left py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Message</th>
                        <th className={`text-left py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Audience</th>
                        <th className={`text-center py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Status</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Sent</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Opens</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Open %</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Clicks</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>CTR</th>
                        <th className={`text-right py-2.5 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Last Sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {perMessage.map(msg => (
                        <tr
                          key={msg.id}
                          className={`border-b transition-colors ${dm ? "border-gray-700/50 hover:bg-gray-700/30" : "border-gray-50 hover:bg-gray-50"}`}
                          data-testid={`msg-row-${msg.id}`}
                        >
                          <td className={`py-2.5 px-3 ${dm ? "text-gray-200" : "text-gray-800"}`}>
                            <div className="font-medium truncate max-w-[180px]">{msg.title}</div>
                            <span className={`text-[10px] ${dm ? "text-gray-500" : "text-gray-400"}`}>{msg.recurrence === "annual" ? "Annual" : "One-time"}</span>
                          </td>
                          <td className={`py-2.5 px-3 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                            {AUDIENCE_LABELS[msg.audience_type] || msg.audience_type}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <Badge className={`text-[9px] px-1.5 ${STATUS_STYLES[msg.status] || ""}`}>
                              {msg.status}
                            </Badge>
                          </td>
                          <td className={`py-2.5 px-3 text-right font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>
                            {msg.total_sent}
                          </td>
                          <td className={`py-2.5 px-3 text-right font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>
                            {msg.total_opened}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <RateBadge rate={msg.open_rate} dm={dm} />
                          </td>
                          <td className={`py-2.5 px-3 text-right font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>
                            {msg.total_clicks}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <RateBadge rate={msg.click_rate} dm={dm} threshold={[10, 5]} />
                          </td>
                          <td className={`py-2.5 px-3 text-right ${dm ? "text-gray-500" : "text-gray-400"}`}>
                            {msg.last_sent_at ? new Date(msg.last_sent_at).toLocaleDateString() : "\u2014"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* List Health — Bounce Tracking */}
          <ListHealthSection dm={dm} bounceStats={bounceStats} cleaningList={cleaningList} onCleanList={async () => {
            setCleaningList(true);
            try {
              const res = await axios.post(`${API}/admin/email-analytics/clean-list`, {}, { headers });
              toast.success(`Cleaned ${res.data.deactivated} bouncing emails`);
              fetchAll();
            } catch { toast.error("Failed to clean list"); }
            finally { setCleaningList(false); }
          }} />
        </>
      )}
    </div>
  );
}

function SummaryCard({ dm, icon: Icon, label, value, accent, bg, testId }) {
  return (
    <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>
            <Icon className={`w-5 h-5 ${accent}`} />
          </div>
          <div>
            <p className={`text-[11px] font-medium uppercase tracking-wider ${dm ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
            <p className={`text-xl font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RateBadge({ rate, dm, threshold }) {
  const [high, mid] = threshold || [30, 15];
  const color = rate >= high ? "text-emerald-500" : rate >= mid ? "text-amber-500" : rate > 0 ? "text-red-500" : (dm ? "text-gray-500" : "text-gray-400");
  const Icon = rate >= mid ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${color}`}>
      {rate > 0 && <Icon className="w-3 h-3" />}
      {rate}%
    </span>
  );
}


function ListHealthSection({ dm, bounceStats, cleaningList, onCleanList }) {
  if (!bounceStats) return null;

  const { total_bounces, hard_bounces, soft_bounces, complaints, bounced_emails, bounce_rate } = bounceStats;
  const hasIssues = total_bounces > 0 || complaints > 0;

  return (
    <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`} data-testid="list-health-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
              <ShieldAlert className="w-4 h-4 inline mr-1.5" />
              List Health
            </CardTitle>
            <CardDescription className={`text-xs ${dm ? "text-gray-400" : ""}`}>
              Bounce tracking and automated list hygiene
            </CardDescription>
          </div>
          {hasIssues && (
            <Button
              size="sm"
              onClick={onCleanList}
              disabled={cleaningList}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs"
              data-testid="clean-list-btn"
            >
              {cleaningList ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              Clean List
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Health Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <HealthStat dm={dm} label="Hard Bounces" value={hard_bounces} color="text-red-500" />
          <HealthStat dm={dm} label="Soft Bounces" value={soft_bounces} color="text-amber-500" />
          <HealthStat dm={dm} label="Complaints" value={complaints} color="text-orange-500" />
          <HealthStat dm={dm} label="Bounce Rate" value={`${bounce_rate}%`} color={bounce_rate > 5 ? "text-red-500" : bounce_rate > 2 ? "text-amber-500" : "text-emerald-500"} />
        </div>

        {/* Bounced Emails Table */}
        {bounced_emails && bounced_emails.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="bounced-emails-table">
              <thead>
                <tr className={`border-b ${dm ? "border-gray-700" : "border-gray-100"}`}>
                  <th className={`text-left py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Email</th>
                  <th className={`text-center py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Type</th>
                  <th className={`text-center py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Count</th>
                  <th className={`text-center py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Status</th>
                  <th className={`text-right py-2 px-3 font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Last Bounce</th>
                </tr>
              </thead>
              <tbody>
                {bounced_emails.map((b, i) => (
                  <tr key={i} className={`border-b transition-colors ${dm ? "border-gray-700/50 hover:bg-gray-700/30" : "border-gray-50 hover:bg-gray-50"}`}>
                    <td className={`py-2 px-3 font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>{b.email}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge className={`text-[9px] px-1.5 ${b.bounce_type === "hard" ? "bg-red-500/10 text-red-500" : b.bounce_type === "complaint" ? "bg-orange-500/10 text-orange-500" : "bg-amber-500/10 text-amber-500"}`}>
                        {b.bounce_type}
                      </Badge>
                    </td>
                    <td className={`py-2 px-3 text-center font-mono ${dm ? "text-gray-200" : "text-gray-800"}`}>{b.bounce_count}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge className={`text-[9px] px-1.5 ${b.is_active ? "bg-emerald-500/10 text-emerald-500" : "bg-red-500/10 text-red-500"}`}>
                        {b.is_active ? "active" : "deactivated"}
                      </Badge>
                    </td>
                    <td className={`py-2 px-3 text-right ${dm ? "text-gray-500" : "text-gray-400"}`}>
                      {b.last_bounced_at ? new Date(b.last_bounced_at).toLocaleDateString() : "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`text-center py-6 ${dm ? "text-gray-500" : "text-gray-400"}`}>
            <ShieldAlert className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
            <p className="text-xs">No bounce events recorded</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function HealthStat({ dm, label, value, color }) {
  return (
    <div className={`p-3 rounded-xl ${dm ? "bg-gray-900/50" : "bg-gray-50"}`}>
      <p className={`text-[10px] font-medium uppercase tracking-wider ${dm ? "text-gray-500" : "text-gray-400"}`}>{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
    </div>
  );
}
